import { runQuery, rowsToObjects } from "./analyticsClient.js";
import { uuidToEndpointId, endpointIdToUuid } from "./inventoryRows.js";
import { withTraceReadSlot } from "./traceReadGate.js";
import { decorateConversationTurns } from "./parseApiRequest.js";
import { parseServerTimestamp } from "./traceUtils.js";
import { onAuthIdentityChange } from "./authTokenStore.js";
import { readConversationContent, conversationPartToBlock, MAX_CONTENT_REFERENCES } from "./conversationContent.js";
import { authedFetch } from "./restClient.js";
import { MAX_RANGE_DAYS } from "./rangeLimits.js";
import { toolSafetyByUseId } from "./toolExecutions.js";

const field = (prefix, name) => ({ stepAlias: "x", field: `DIMENSION_FIELD_${prefix}_${name}` });
const eq = (prefix, name, value) => ({ pred: { field: field(prefix, name), op: "OPERATOR_EQ", value } });
const dimensions = (prefix, names) => names.map(([name, outputAlias]) => ({ field: field(prefix, name), outputAlias }));
const NIL_ID = "00000000-0000-0000-0000-000000000000";
const MAX_TEXT_UNITS = 8 * 1024 * 1024;
// One content read carries up to the server's reference cap. Servers from
// before the cap was raised refuse anything above LEGACY_CONTENT_BATCH; the
// reader learns that once and stays at the older size for the session.
const CONTENT_BATCH = MAX_CONTENT_REFERENCES;
const LEGACY_CONTENT_BATCH = 64;
let contentBatch = CONTENT_BATCH;
function contentSize(value) {
    if (typeof value === "string") return value.length;
    if (!value || typeof value !== "object") return 8;
    return Object.entries(value).reduce((total, [key, item]) => total + key.length + contentSize(item), 0);
}

function partReference(row) {
    return { messagePart: {
        sourceEventId: uuidToEndpointId(row.sourceEventId), messageId: uuidToEndpointId(row.messageId), ordinal: Number(row.ordinal),
    } };
}

// Immutable message-part cache, bounded by count and text size. Clear tenant
// content on identity changes so repeated pages avoid duplicate body reads.
const ITEM_CACHE = new Map();
export const ITEM_CACHE_MAX_ITEMS = 6000;
export const ITEM_CACHE_MAX_UNITS = 48 * 1024 * 1024;
// Content batches a single read keeps in flight. The trace read gate is shared
// with every other read on the page, so a long conversation must not hold
// every slot and cut ahead of whatever else the reader just opened.
export const READ_BATCH_CONCURRENCY = 3;
let itemCacheUnits = 0;
let itemCacheMaxItems = ITEM_CACHE_MAX_ITEMS;
let itemCacheMaxUnits = ITEM_CACHE_MAX_UNITS;
let maxTextUnits = MAX_TEXT_UNITS;
onAuthIdentityChange(() => { ITEM_CACHE.clear(); itemCacheUnits = 0; });

export function _resetConversationItemCacheForTests() {
    ITEM_CACHE.clear();
    itemCacheUnits = 0;
    itemCacheMaxItems = ITEM_CACHE_MAX_ITEMS;
    itemCacheMaxUnits = ITEM_CACHE_MAX_UNITS;
    maxTextUnits = MAX_TEXT_UNITS;
    contentBatch = CONTENT_BATCH;
}
export function _setConversationReadLimitsForTests({ items = itemCacheMaxItems, units = itemCacheMaxUnits, textUnits = maxTextUnits } = {}) {
    itemCacheMaxItems = items;
    itemCacheMaxUnits = units;
    maxTextUnits = textUnits;
}
export function _conversationItemCacheSizeForTests() {
    return { items: ITEM_CACHE.size, units: itemCacheUnits };
}

// A message part is addressed by the capture that shipped it, its message and
// its ordinal, and never rewritten under that address, so the address is as
// good a cache key as an item's content hash.
function partCacheKey(row) {
    return row?.sourceEventId && row?.messageId ? `part:${row.sourceEventId}:${row.messageId}:${Number(row.ordinal)}` : null;
}

const PART_BODY = { reference: partReference, cacheKey: partCacheKey, unwrap: (entry) => entry.messagePart };

function readCachedItem(key) {
    const hit = ITEM_CACHE.get(key);
    if (!hit) return undefined;
    ITEM_CACHE.delete(key);
    ITEM_CACHE.set(key, hit);
    return hit.item;
}

function writeCachedItem(key, item, units) {
    if (units > itemCacheMaxUnits / 4) return;
    while (ITEM_CACHE.size && (ITEM_CACHE.size >= itemCacheMaxItems || itemCacheUnits + units > itemCacheMaxUnits)) {
        const oldest = ITEM_CACHE.keys().next().value;
        itemCacheUnits -= ITEM_CACHE.get(oldest).units;
        ITEM_CACHE.delete(oldest);
    }
    ITEM_CACHE.set(key, { item, units });
    itemCacheUnits += units;
}

// Run `tasks` with at most `limit` in flight. The first failure stops the
// others from starting anything further and is rethrown once the ones already
// running have settled.
async function runPooled(tasks, limit) {
    let next = 0;
    let failure = null;
    const worker = async () => {
        while (next < tasks.length && !failure) {
            const task = tasks[next++];
            try { await task(); } catch (error) { failure = failure || error; return; }
        }
    };
    await Promise.all(Array.from({ length: Math.min(limit, tasks.length) }, worker));
    if (failure) throw failure;
}

function chunks(list, size) {
    const out = [];
    for (let start = 0; start < list.length; start += size) out.push(list.slice(start, start + size));
    return out;
}

// A refused batch is retried as smaller ones. A server still enforcing the
// older reference cap refuses a larger batch as INVALID_ARGUMENT; the reader
// retries it at that size and, once that succeeds, stays there for the rest
// of the session. An unrelated INVALID_ARGUMENT fails again at the old size
// and is rethrown without changing the batch. A batch whose bodies exceed the
// server's byte limit is RESOURCE_EXHAUSTED and is halved until it fits or
// reaches MIN_SPLIT_BATCH, below which the read fails as it would have at the
// old batch size. The floor bounds the unwinding: a full batch costs at most
// 31 sequential requests before it lands or fails. Any other failure is
// rethrown as is.
const MIN_SPLIT_BATCH = 16;
const refusedLegacyCap = (error, size) => error?.grpcCode === "INVALID_ARGUMENT" && size > LEGACY_CONTENT_BATCH;
const refusedBytes = (error, size) => error?.grpcCode === "RESOURCE_EXHAUSTED" && size > MIN_SPLIT_BATCH;
// The content endpoint refuses a whole batch (FAILED_PRECONDITION, "selected
// conversation evidence is missing or changed") when a reference no longer
// names the message's current representation. On a live session that is the
// normal case for a read that straddles a capture: the request that landed
// between the message read and the content read re-shipped the history and
// became the current source of every message in it. The remedy is a second
// read from fresh rows, once; a refusal that survives that is a real failure.
const evidenceMoved = (error) => error?.grpcCode === "FAILED_PRECONDITION";
async function readingCurrentEvidence(read) {
    try {
        return await read();
    } catch (error) {
        if (!evidenceMoved(error)) throw error;
        return read();
    }
}
async function readBatch(content, references) {
    if (references.length > contentBatch) return readPieces(content, chunks(references, contentBatch));
    try {
        return await content(references);
    } catch (error) {
        if (refusedLegacyCap(error, references.length)) {
            const contents = await readPieces(content, chunks(references, LEGACY_CONTENT_BATCH));
            contentBatch = LEGACY_CONTENT_BATCH;
            return contents;
        }
        if (refusedBytes(error, references.length)) return readPieces(content, chunks(references, Math.ceil(references.length / 2)));
        throw error;
    }
}
async function readPieces(content, pieces) {
    const contents = [];
    for (const piece of pieces) contents.push(...await readBatch(content, piece));
    return contents;
}

// The bodies for `rows`, in row order: cached bodies answered from the cache,
// the rest fetched in content batches, a few at a time, under the trace read
// gate. `shape` says how a row is addressed and which body the endpoint
// returns for it. `alongside` are other reads the caller wants in the same
// pool, so the gate sees one bounded reader. The text budget counts fetched
// text only -- cached text was already admitted once -- and is checked as each
// batch lands, so an over-budget conversation stops asking instead of paying
// for every batch first.
// Either ceiling a read can hit -- the text budget below, or the parts
// ceiling on a record read -- as its own error, so a paged reader can tell
// "too much for one view" from a failed read and shrink the page instead of
// giving up.
class RecordTooLargeError extends Error {
    constructor() {
        super("This conversation exceeds the full-view limit. Read selected items through conversation content.");
        this.name = "RecordTooLargeError";
        this.recordTooLarge = true;
    }
}

async function readBodies(content, rows, shape, { alongside = [] } = {}) {
    const bodies = new Array(rows.length);
    const missing = [];
    rows.forEach((row, index) => {
        const key = shape.cacheKey(row);
        const cached = key ? readCachedItem(key) : undefined;
        if (cached !== undefined) bodies[index] = cached;
        else missing.push(index);
    });
    const batches = chunks(missing, contentBatch);
    let size = 0;
    const bodyTask = (indexes) => async () => {
        const batch = await readBatch(content, indexes.map((index) => shape.reference(rows[index])));
        size += contentSize(batch);
        if (size > maxTextUnits) throw new RecordTooLargeError();
        batch.forEach((entry, i) => {
            const index = indexes[i];
            const item = shape.unwrap(entry);
            bodies[index] = item;
            const key = shape.cacheKey(rows[index]);
            if (key && item) writeCachedItem(key, item, contentSize(item));
        });
    };
    await runPooled([...alongside, ...batches.map(bodyTask)], READ_BATCH_CONCURRENCY);
    return bodies;
}

// The two gated readers every conversation read goes through: analytics rows
// and content bodies, each holding a trace-read slot for the caller's signal.
function readers({ traceId, timeRange, signal, requestContext = null }) {
    const query = (spec) => withTraceReadSlot(signal, () => runQuery(spec, { signal, ...(requestContext === null ? {} : { cache: false, requestContext }) })).then(rowsToObjects);
    const content = (references) => withTraceReadSlot(signal, () => readConversationContent({
        traceId: uuidToEndpointId(traceId), timeRange, references,
    }, { signal }));
    return { query, content };
}

async function loadInvocation(query, { traceId, sourceEventId, timeRange }) {
    const invocations = await query({
        root: { alias: "x", entity: "ENTITY_TYPE_MODEL_INVOCATION" }, timeRange,
        filter: { andExpr: { children: [eq("MODEL_INVOCATION", "TRACE_ID", traceId), eq("MODEL_INVOCATION", "SOURCE_EVENT_ID", sourceEventId)] } },
        output: { dimensions: dimensions("MODEL_INVOCATION", [
            ["INVOCATION_ID", "invocationId"], ["SOURCE_EVENT_ID", "sourceEventId"], ["SOURCE_CAPTURE", "sourceCapture"],
            ["SOURCE_OMITTED_ITEM_COUNT", "sourceOmittedItems"], ["SOURCE_OMITTED_BYTE_COUNT", "sourceOmittedBytes"],
            ["MANIFEST_ITEM_COUNT", "manifestItemCount"],
            ["REQUEST_TEMPERATURE", "requestTemperature"], ["REQUEST_MAX_TOKENS", "requestMaxTokens"],
            ["REQUEST_STREAM", "requestStream"], ["REQUEST_THINKING_EFFORT", "requestThinkingEffort"],
        ]) }, limit: 2,
    });
    if (invocations.length !== 1) throw new Error("A unique normalized invocation is unavailable for this capture.");
    return invocations[0];
}

// Request settings remain queryable after exact per-call content is retired.
export async function readInvocationParameters({ traceId, sourceEventId, timeRange, signal } = {}) {
    if (!traceId || !sourceEventId || !timeRange) throw new Error("Conversation identity and time range are required.");
    const { query } = readers({ traceId, timeRange, signal });
    const invocation = await loadInvocation(query, { traceId, sourceEventId, timeRange });
    return {
        turns: [], toolDefs: [], tools: [], invocation,
        settings: { temperature: invocation.requestTemperature ?? null, maxOutputTokens: invocation.requestMaxTokens ?? null,
            stream: invocation.requestStream ?? null, reasoningEffort: invocation.requestThinkingEffort ?? "" },
        partialBody: Number(invocation.sourceOmittedItems) > 0 || Number(invocation.sourceOmittedBytes) > 0,
        requestContextRetired: true,
    };
}

// Trigger previews use the messages attributed to the selected observation.
// Mark the missing request context explicitly so previews cannot claim parity
// with the complete input once supplied to the model.
export async function readObservedCapture({ traceId, sourceEventId, timeRange, signal, requestContext = null } = {}) {
    if (!traceId || !sourceEventId || !timeRange) throw new Error("Conversation identity and time range are required.");
    const { query, content } = readers({ traceId, timeRange, signal, requestContext });
    const bound = { andExpr: { children: [
        eq("CONVERSATION_MESSAGE", "TRACE_ID", traceId),
        eq("CONVERSATION_MESSAGE", "SOURCE_EVENT_ID", sourceEventId),
    ] } };
    // The page spec reads newest first; turns are assembled oldest first, as
    // the record page reverses its rows before assembling them.
    const newestFirst = await query(messagePageSpec(traceId, timeRange, bound, RECORD_MESSAGE_WINDOW));
    const messages = newestFirst.slice().reverse();
    const turns = await assembleRecordTurns({ query, content, traceId, timeRange, messages });
    return { turns, tools: [], toolDefs: [], partialBody: true, requestContextRetired: true,
        partialBodyReason: "Only observed messages are available; exact model-call context is no longer retained.",
        truncated: messages.length === RECORD_MESSAGE_WINDOW };
}

export const RECORD_PAGE_MESSAGES = 1024;
// Message ids per part lookup, parts per page of that lookup (a full page is
// followed by the next, never taken as the end), and the most parts one
// page read carries before it is refused as too large for a single view.
// The lookup resolves the whole trace's parts before the id filter applies, so
// its cost is per QUERY, not per id: one lookup carries every id a page can
// hold (the service accepts up to 1,024 values in a list), and a record with
// thousands of messages costs a page one or two resolver passes instead of
// eight running three abreast.
const RECORD_ID_CHUNK = 1024;
const RECORD_PARTS_PAGE = 1000;
export const RECORD_MAX_PARTS = 4096;

// The record up to a capture: its conversation, and every message whose
// referencing capture is no later than `before` (a capture event time, ISO).
function recordBound(traceId, before) {
    return { andExpr: { children: [
        eq("CONVERSATION_MESSAGE", "TRACE_ID", traceId),
        ...(before ? [{ pred: { field: field("CONVERSATION_MESSAGE", "EVENT_TIME"), op: "OPERATOR_LTE", value: before } }] : []),
    ] } };
}

// How many distinct messages the record holds up to `before`. One aggregate,
// trace-bound; null when the count is unavailable. Read alongside a remainder,
// never on its own per trace open: the number only matters once there are
// earlier messages to say something about.
export async function countConversationMessages({ traceId, timeRange, before = null, signal } = {}) {
    if (!traceId || !timeRange) throw new Error("Conversation identity and time range are required.");
    const { query } = readers({ traceId, timeRange, signal });
    return countMessages(query, traceId, timeRange, before);
}

async function countMessages(query, traceId, timeRange, before) {
    const [row] = await query({
        root: { alias: "x", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" }, timeRange, filter: recordBound(traceId, before),
        output: { metrics: [{ outputAlias: "messages", computed: { field: field("CONVERSATION_MESSAGE", "MESSAGE_ID"), op: "AGGREGATE_OP_COUNT_DISTINCT" } }] },
        limit: 1,
    });
    const total = Number(row?.messages);
    return Number.isFinite(total) ? total : null;
}

// The record's message rows, newest first, under `bound`.
function messagePageSpec(traceId, timeRange, bound, limit) {
    return {
        root: { alias: "x", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" }, timeRange, filter: bound,
        output: { dimensions: dimensions("CONVERSATION_MESSAGE", [
            ["MESSAGE_ID", "messageId"], ["EVENT_TIME", "eventTime"], ["SOURCE_ORDINAL", "ordinal"], ["ROLE", "role"],
            ["SOURCE_EVENT_ID", "sourceEventId"], ["MESSAGE_AT", "messageAt"],
        ]) },
        orderBy: [{ outputAlias: "eventTime", direction: "DIRECTION_DESC" }, { outputAlias: "ordinal", direction: "DIRECTION_DESC" }],
        limit,
    };
}

// Messages -> turns, oldest first: their parts in bounded pages, the parts'
// bodies through the content reader, one turn per message that rendered a
// block. `flags(message)` adds the caller's per-turn marks (the remainder's
// capture split; a session page adds none). Refuses a set whose parts exceed
// what one view carries.
async function assembleRecordTurns({ query, content, traceId, timeRange, messages }, flags = () => ({})) {
    const parts = [];
    await runPooled(chunks(messages.map((message) => message.messageId), RECORD_ID_CHUNK).map((ids) => async () => {
        for (let offset = 0; ; offset += RECORD_PARTS_PAGE) {
            const page = await query({
                root: { alias: "x", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE_PART" }, timeRange,
                filter: { andExpr: { children: [
                    eq("CONVERSATION_MESSAGE_PART", "TRACE_ID", traceId),
                    { pred: { field: field("CONVERSATION_MESSAGE_PART", "MESSAGE_ID"), op: "OPERATOR_IN", value: ids } },
                ] } },
                output: { dimensions: dimensions("CONVERSATION_MESSAGE_PART", [
                    ["MESSAGE_ID", "messageId"], ["ORDINAL", "ordinal"], ["KIND", "kind"], ["SOURCE_EVENT_ID", "sourceEventId"],
                ]) },
                orderBy: [{ outputAlias: "messageId", direction: "DIRECTION_ASC" }, { outputAlias: "ordinal", direction: "DIRECTION_ASC" }],
                limit: RECORD_PARTS_PAGE, offset,
            });
            parts.push(...page);
            if (parts.length > RECORD_MAX_PARTS) throw new RecordTooLargeError();
            if (page.length < RECORD_PARTS_PAGE) break;
        }
    }), READ_BATCH_CONCURRENCY);
    const partKeys = new Set();
    const uniqueParts = parts.filter((part) => {
        const key = `${part.messageId}:${Number(part.ordinal)}`;
        if (!part.messageId || partKeys.has(key)) return false;
        partKeys.add(key);
        return true;
    }).sort((a, b) => Number(a.ordinal) - Number(b.ordinal));
    const bodies = await readBodies(content, uniqueParts, PART_BODY);
    const blocksByMessage = new Map();
    uniqueParts.forEach((part, index) => {
        const body = bodies[index];
        if (!body) throw new Error("Conversation content is incomplete.");
        const block = conversationPartToBlock(body);
        if (!block) return;
        if (!blocksByMessage.has(part.messageId)) blocksByMessage.set(part.messageId, []);
        blocksByMessage.get(part.messageId).push(block);
    });

    const turns = [];
    messages.forEach((message, index) => {
        const blocks = blocksByMessage.get(message.messageId) || [];
        if (!blocks.length) return;
        const at = parseServerTimestamp(message.messageAt);
        const captured = parseServerTimestamp(message.eventTime);
        const shippedBy = message.sourceEventId || null;
        turns.push({
            role: message.role || "unknown", label: message.role || "unknown", blocks,
            messageId: message.messageId, invocationId: null, sourceEventId: shippedBy, contentSha256: null,
            ordinal: index, usage: "", sectionKind: "conversation",
            messageAt: Number.isFinite(at) ? at : null, observationSourceEventId: shippedBy,
            // When the capture that ships this message was observed: a fact
            // about the capture, not a date for the message (that is
            // messageAt, null when the source did not say). It orders the
            // record's captures for readers that need the axis dated even
            // where messages are not, such as the spawn join's window.
            capturedAt: Number.isFinite(captured) ? captured : null,
            ...flags(message),
        });
    });
    return decorateConversationTurns(turns);
}

// -- the record as an axis: pages ---------------------------------------------
//
// A session-first reading has no focal capture. Its axis is the record itself,
// read newest first in pages: the newest messages open the tab, and "earlier"
// prepends the page before them. Two facts about the index shape the paging:
//
// * The index holds one row per OBSERVATION of a message -- every capture that
//   re-shipped it -- and the axis orders a message by its newest observation.
//   Paging by time alone repeats messages: an OLDER observation of a message
//   the previous page already showed passes the next page's time bound. So a
//   page carries `exclude`, every id already on the axis, and drops repeats.
// * Many messages share one capture time (a capture ships several, and a
//   re-shipping capture stamps hundreds). A time bound alone cannot cut
//   between them, so the cursor is a keyset (event time, ordinal) and the bound
//   is strict: older times, or the same time at a lower ordinal.
//
// A page short of `maxMessages` after dropping repeats refills from the next
// keyset window, bounded, so a page is full or the record is exhausted --
// never short because of duplicates. A page whose parts or text exceed what
// one read carries is halved and read as two; a single message over the
// ceiling renders as a turn that says so rather than sinking the page.

// Rows one message query returns at most: the analytics service bounds a
// trace-detail record query at this many rows, so a page walks the keyset in
// windows of it. Windows per page: each is one bounded query; the ceiling
// keeps a pathological record (every candidate an old observation of a shown
// message) from turning one page into an unbounded walk. A full page with no
// repeats takes two windows.
// Where the messages a finding cites sit in the record, and a cursor placed
// just after one of them.
//
// The record reads backwards from a session's newest message, which is
// unreadable for a long one: a verification pass over two staging sessions
// loaded 4,096 of 29,010 messages and called 23 of 33 findings unverifiable,
// not because the evidence was absent but because it lay outside the window
// the reader could afford.
//
// Two things make this fiddly. The cursor is exclusive, so anchoring it on the
// citation itself drops the citation AND the tool result that follows it --
// and the result is what an effect is settled on. And the server takes the
// message's own 16-byte id, not a timestamp, so the anchor has to be a message
// that really exists. So: find the cited messages, then take a real message a
// little after one of them and anchor there.
export const CITATION_LEAD_MESSAGES = 200;

const KEYSET = [["MESSAGE_ID", "messageId"], ["EVENT_TIME", "eventTime"], ["SOURCE_ORDINAL", "ordinal"]];
const citedQuery = (signal) => (spec) => withTraceReadSlot(signal, () => runQuery(spec, { signal })).then(rowsToObjects);

// Every cited message the record holds, newest first, as
// { messageId, ms, eventTime, ordinal }. Ids the record does not hold are left
// out.
export async function citationKeysets({ traceId, timeRange, messageIds = [], signal } = {}) {
    const ids = [...new Set(messageIds.map((id) => String(id || "").toLowerCase()).filter(Boolean))];
    if (!traceId || !timeRange || !ids.length) return [];
    const ask = citedQuery(signal);
    const trace = eq("CONVERSATION_MESSAGE", "TRACE_ID", traceId);
    const found = [];
    for (let start = 0; start < ids.length; start += 500) {
        const rows = await ask({
            root: { alias: "x", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" }, timeRange,
            filter: { andExpr: { children: [trace, {
                pred: { field: field("CONVERSATION_MESSAGE", "MESSAGE_ID"), op: "OPERATOR_IN", value: ids.slice(start, start + 500) },
            }] } },
            output: { dimensions: dimensions("CONVERSATION_MESSAGE", KEYSET) },
            orderBy: [{ outputAlias: "eventTime", direction: "DIRECTION_DESC" }, { outputAlias: "ordinal", direction: "DIRECTION_DESC" }],
            limit: 500,
        });
        for (const row of rows) {
            const ms = parseServerTimestamp(row.eventTime);
            if (Number.isFinite(ms)) found.push({ messageId: String(row.messageId || "").toLowerCase(), ms, eventTime: row.eventTime, ordinal: Number(row.ordinal) || 0 });
        }
    }
    return found.sort((a, b) => b.ms - a.ms || b.ordinal - a.ordinal);
}

// A cursor on a real message `lead` places after the one at
// (`eventTime`, `ordinal`), so a page read before it holds the cited message
// and what came of it. Null when fewer than `lead` messages follow: the
// citation is near the end and the newest page is already the right window.
//
// After the keyset, not after the time. A session shipped in a batch gives
// hundreds of messages one EVENT_TIME; "later than the citation's time" skips
// the rest of its batch, lands the anchor past it, and a slice read back from
// there can stop short of the citation. Every citation in that batch then got
// the same anchor and the same slice.
export async function cursorAfter({ traceId, timeRange, eventTime, ordinal = 0, signal, lead = CITATION_LEAD_MESSAGES } = {}) {
    if (!traceId || !timeRange || !eventTime) return null;
    const time = field("CONVERSATION_MESSAGE", "EVENT_TIME");
    const after = await citedQuery(signal)({
        root: { alias: "x", entity: "ENTITY_TYPE_CONVERSATION_MESSAGE" }, timeRange,
        filter: { andExpr: { children: [eq("CONVERSATION_MESSAGE", "TRACE_ID", traceId), { orExpr: { children: [
            { pred: { field: time, op: "OPERATOR_GT", value: eventTime } },
            { andExpr: { children: [
                { pred: { field: time, op: "OPERATOR_EQ", value: eventTime } },
                { pred: { field: field("CONVERSATION_MESSAGE", "SOURCE_ORDINAL"), op: "OPERATOR_GT", value: Number(ordinal) || 0 } },
            ] } },
        ] } }] } },
        output: { dimensions: dimensions("CONVERSATION_MESSAGE", KEYSET) },
        orderBy: [{ outputAlias: "eventTime", direction: "DIRECTION_ASC" }, { outputAlias: "ordinal", direction: "DIRECTION_ASC" }],
        limit: lead,
    });
    const anchor = after[after.length - 1];
    if (!anchor || after.length < lead) return null;
    const ms = parseServerTimestamp(anchor.eventTime);
    if (!Number.isFinite(ms)) return null;
    return {
        eventTime: new Date(ms).toISOString(),
        ordinal: Number(anchor.ordinal) || 0,
        messageId: uuidToEndpointId(String(anchor.messageId || "").toLowerCase()),
    };
}

export const RECORD_MESSAGE_WINDOW = 1000;
export const RECORD_PAGE_REFILL_ROUNDS = 8;

// The keyset bound: strictly before `cursor` = { eventTime (ISO), ordinal }.
function recordKeysetBound(traceId, cursor) {
    const trace = eq("CONVERSATION_MESSAGE", "TRACE_ID", traceId);
    if (!cursor) return { andExpr: { children: [trace] } };
    const time = field("CONVERSATION_MESSAGE", "EVENT_TIME");
    const ordinal = field("CONVERSATION_MESSAGE", "SOURCE_ORDINAL");
    return { andExpr: { children: [
        trace,
        { orExpr: { children: [
            { pred: { field: time, op: "OPERATOR_LT", value: cursor.eventTime } },
            { andExpr: { children: [
                { pred: { field: time, op: "OPERATOR_EQ", value: cursor.eventTime } },
                { pred: { field: ordinal, op: "OPERATOR_LT", value: Number(cursor.ordinal) } },
            ] } },
        ] } },
    ] } };
}

// The keyset a row stands at, in the form the bound compares against.
export function recordCursorOf(row) {
    if (!row) return null;
    const ms = parseServerTimestamp(row.eventTime);
    if (!Number.isFinite(ms)) return null;
    return { eventTime: new Date(ms).toISOString(), ordinal: Number(row.ordinal) || 0 };
}

// One page of the record before `cursor` (null = the newest page), as turns
// oldest first. Returns { turns, messageCount, total, nextCursor, exhausted }:
// `nextCursor` addresses the page before this one and is null once the record
// is exhausted; `total` is the record's distinct message count (null when the
// count is unavailable) and is read once, on the newest page.
// `focalEventId` names the request whose axis this page is for: the messages
// that request shipped are marked `fromThisCapture`, so a request tab can tell
// its own messages from the conversation before it. A session page names none.
export async function readConversationPage({ toolSafety = false, ...read } = {}) {
    const page = await readRecordPage(read);
    return toolSafety ? attachToolSafety(page, read) : page;
}

export const SAFETY_IDS_PER_READ = 100;
export const SAFETY_ROWS_PER_READ = 1000;

const isSafetyTarget = (block) => block?.type === "tool_use" && !block.virtual && Boolean(block.id);

function safetySpec(traceId, timeRange, useIds) {
    const f = (name) => ({ stepAlias: "s", field: `DIMENSION_FIELD_${name}` });
    return {
        root: { alias: "s", entity: "ENTITY_TYPE_TRACE_TOOL_SAFETY" }, timeRange,
        filter: { andExpr: { children: [
            { pred: { field: f("TRACE_ACTIVITY_TRACE_ID"), op: "OPERATOR_EQ", value: traceId } },
            { pred: { field: f("TRACE_TOOL_USE_ID"), op: "OPERATOR_IN", value: useIds } },
        ] } },
        output: { dimensions: [
            ["TRACE_ACTIVITY_EVENT_TIME", "event_time"], ["TRACE_ACTIVITY_EVENT_ID", "event_id"], ["TRACE_TOOL_USE_ID", "tool_use_id"],
            ["TRACE_SAFETY_CATEGORY", "category"], ["TRACE_SAFETY_DECISION", "decision"], ["TRACE_SAFETY_RULE_SOURCE", "rule_source"],
            ["TRACE_SAFETY_REASON_CODE", "reason_code"], ["TRACE_SAFETY_DECISION_SOURCE", "decision_source"],
            ["TRACE_SAFETY_HOOK_EVENT", "hook_event"], ["TRACE_SAFETY_SANDBOX_VIOLATION_COUNT", "sandbox_violation_count"],
        ].map(([name, outputAlias]) => ({ field: f(name), outputAlias })) },
        orderBy: [{ outputAlias: "event_time", direction: "DIRECTION_ASC" }, { outputAlias: "event_id", direction: "DIRECTION_ASC" }],
        limit: SAFETY_ROWS_PER_READ,
    };
}

function analyticsRange(timeRange) {
    const start = Date.parse(timeRange.absolute?.start);
    const end = Date.parse(timeRange.absolute?.end);
    const maxRangeMs = MAX_RANGE_DAYS * 86400000;
    return end - start > maxRangeMs
        ? { absolute: { start: new Date(end - maxRangeMs).toISOString(), end: timeRange.absolute.end } }
        : timeRange;
}

const HOUR_MS = 3600000;
export const SAFETY_WINDOW_BEFORE_MS = HOUR_MS;
export const SAFETY_WINDOW_AFTER_MS = HOUR_MS;
export const SAFETY_WINDOW_BEFORE_CAPTURE_MS = 24 * HOUR_MS;

function safetyWindow(turns, timeRange) {
    let start = Infinity;
    let end = -Infinity;
    for (const turn of turns) {
        if (!(turn.blocks || []).some(isSafetyTarget)) continue;
        const span = Number.isFinite(turn.messageAt)
            ? [turn.messageAt - SAFETY_WINDOW_BEFORE_MS, turn.messageAt + SAFETY_WINDOW_AFTER_MS]
            : Number.isFinite(turn.capturedAt)
                ? [turn.capturedAt - SAFETY_WINDOW_BEFORE_CAPTURE_MS, turn.capturedAt + SAFETY_WINDOW_AFTER_MS]
                : null;
        if (!span) return analyticsRange(timeRange);
        start = Math.min(start, span[0]);
        end = Math.max(end, span[1]);
    }
    if (!Number.isFinite(start)) return analyticsRange(timeRange);
    return analyticsRange({ absolute: { start: new Date(start).toISOString(), end: new Date(end).toISOString() } });
}

async function attachToolSafety(page, { traceId, timeRange, signal }) {
    const useIds = [...new Set(page.turns.flatMap((turn) => (turn.blocks || []).filter(isSafetyTarget).map((block) => String(block.id))))];
    if (!useIds.length) return page;
    const { query } = readers({ traceId, timeRange, signal });
    const range = safetyWindow(page.turns, timeRange);
    const chunks = [];
    for (let i = 0; i < useIds.length; i += SAFETY_IDS_PER_READ) chunks.push(useIds.slice(i, i + SAFETY_IDS_PER_READ));
    const reads = await Promise.all(chunks.map((chunk) => query(safetySpec(traceId, range, chunk))
        .then((rows) => ({ chunk, rows }), () => ({ chunk, rows: null }))));
    const found = new Map();
    const status = new Map();
    for (const { chunk, rows } of reads) {
        const read = !rows ? "failed" : rows.length >= SAFETY_ROWS_PER_READ ? "incomplete" : "complete";
        for (const id of chunk) status.set(id, read);
        for (const [id, list] of toolSafetyByUseId(rows)) found.set(id, list);
    }
    const turns = page.turns.map((turn) => ({ ...turn, blocks: (turn.blocks || []).map((block) => (isSafetyTarget(block)
        ? { ...block, safety: found.get(String(block.id)) || [], safetyRead: status.get(String(block.id)) }
        : block)) }));
    return { ...page, turns };
}

async function readRecordPage({ traceId, timeRange, signal, cursor = null, exclude = [], maxMessages = RECORD_PAGE_MESSAGES, focalEventId = null, aroundMessageId = null, following = 0 } = {}) {
    if (!traceId || !timeRange) throw new Error("Conversation identity and time range are required.");
    // The record read is the page in one call: messages with their bodies,
    // keyed by trace and an observation cursor, deduplicated and scoped
    // server-side. A gateway without the route (a dashboard deployed ahead of
    // it) is remembered for the session and the composed read below stands in.
    if (recordRouteAvailable !== false) {
        try {
            return await readConversationRecordPage({ traceId, timeRange, signal, cursor, maxMessages, focalEventId, aroundMessageId, following });
        } catch (error) {
            if (!error?.recordRouteMissing) throw error;
            recordRouteAvailable = false;
        }
    }
    // The composed reader uses analytics entities with a narrower range cap
    // than the dedicated record route. Keep its existing recent-window view.
    return readingCurrentEvidence(() => readPageOnce({ traceId, timeRange: analyticsRange(timeRange), signal, cursor, exclude, maxMessages, focalEventId }));
}

// Whether the gateway serves the record read; null until the first answer.
let recordRouteAvailable = null;
export function _resetRecordRouteForTests() {
    recordRouteAvailable = null;
}

// One page of the record through `conversations:readRecord`: the same
// { turns, messageCount, total, nextCursor, exhausted } the composed read
// returns, so the callers do not know which read served them. The cursor is
// the record's observation keyset in either direction.
//
// `aroundMessageId` reads the page holding that message, with up to `following`
// newer ones after it, placed by the record itself: a position taken from
// another view of a re-shipped message need not be where the record keeps it.
// `aroundHonoured` says whether the server did; one that predates the field
// serves the newest page, and the caller must not read that as the message's.
export async function readConversationRecordPage({ traceId, timeRange, signal, cursor = null, maxMessages = RECORD_PAGE_MESSAGES, focalEventId = null, aroundMessageId = null, following = 0 } = {}) {
    const traceBytes = uuidToEndpointId(traceId);
    const around = aroundMessageId ? uuidToEndpointId(String(aroundMessageId).toLowerCase()) : null;
    const body = {
        traceId: traceBytes,
        timeRange,
        limit: maxMessages,
        includeSourceFacts: true,
        // The cursor is the record's own total order: time, ordinal, and the
        // message at that position, since messages share the first two.
        ...(!around && cursor?.eventTime ? { before: {
            observedAt: new Date(cursor.eventTime).toISOString(),
            sourceOrdinal: Number(cursor.ordinal) || 0,
            ...(cursor.messageId ? { messageId: cursor.messageId } : {}),
        } } : {}),
        ...(around ? { aroundMessageId: around, ...(following > 0 ? { following } : {}) } : {}),
    };
    const page = await fetchConversationRecord(body, signal);
    const messages = Array.isArray(page?.messages) ? page.messages : [];
    const sourceIds = new Set(messages.map(message => message.sourceEventId ? endpointIdToUuid(message.sourceEventId) : null).filter(Boolean));
    const reportedAttributions = Array.isArray(page?.clientAttributions) ? page.clientAttributions : [];
    const hasAttributions = Array.isArray(page?.clientAttributions);
    const attributionBySource = new Map();
    for (const entry of reportedAttributions.slice(0, MAX_RECORD_CLIENT_ATTRIBUTIONS)) {
        const normalized = normalizeAttribution(entry);
        const { sourceEventId } = normalized;
        if (!sourceIds.has(sourceEventId)) continue;
        // A duplicate source must not select an arbitrary interpretation.
        attributionBySource.set(sourceEventId, attributionBySource.has(sourceEventId)
            ? { sourceEventId, invocationId: null, factBundles: [] }
            : normalized);
    }
    const clientAttributions = hasAttributions ? [...sourceIds].map(sourceEventId =>
        attributionBySource.get(sourceEventId) || { sourceEventId, invocationId: null, factBundles: [] }) : null;
    const focal = focalEventId ? String(focalEventId).toLowerCase() : null;
    const turns = [];
    messages.forEach((message, index) => {
        const blocks = (Array.isArray(message.parts) ? message.parts : [])
            .map((part) => (part.elided ? elidedPartBlock(part) : conversationPartToBlock(part.body)))
            .filter(Boolean);
        if (!blocks.length) return;
        const at = message.messageAt ? Date.parse(message.messageAt) : NaN;
        const captured = message.observedAt ? Date.parse(message.observedAt) : NaN;
        const firstReceived = message.firstReceivedAt ? Date.parse(message.firstReceivedAt) : NaN;
        const shippedBy = message.sourceEventId ? endpointIdToUuid(message.sourceEventId) : null;
        turns.push({
            role: message.role || "unknown", label: message.role || "unknown", blocks,
            messageId: endpointIdToUuid(message.messageId), invocationId: null, sourceEventId: shippedBy, contentSha256: null,
            ordinal: index, usage: "", sectionKind: "conversation",
            messageAt: Number.isFinite(at) ? at : null, observationSourceEventId: shippedBy,
            capturedAt: Number.isFinite(captured) ? captured : null,
            captureCompleteness: message.completeness || null,
            // Where `messageAt` came from, the capture that carried it, and
            // when Origin first received the message: a server that predates
            // these fields leaves them null and the stamp stays generic.
            messageTimeSource: message.messageTimeSource || null,
            messageTimeEventId: message.messageTimeEventId ? endpointIdToUuid(message.messageTimeEventId) : null,
            firstReceivedAt: Number.isFinite(firstReceived) ? firstReceived : null,
            firstReceivedEventId: message.firstReceivedEventId ? endpointIdToUuid(message.firstReceivedEventId) : null,
            ...(focal ? { fromThisCapture: String(shippedBy || "").toLowerCase() === focal } : {}),
        });
    });
    const next = page?.next?.observedAt
        ? {
            eventTime: new Date(page.next.observedAt).toISOString(),
            ordinal: Number(page.next.sourceOrdinal) || 0,
            ...(page.next.messageId ? { messageId: page.next.messageId } : {}),
        }
        : null;
    // The record's size rides on the newest page only; a page read before a
    // cursor carries 0, and the caller keeps the figure it already has.
    const total = Number(page?.totalMessages);
    return {
        turns: decorateConversationTurns(turns),
        messageCount: messages.length,
        total: Number.isFinite(total) && total > 0 ? total : null,
        nextCursor: next,
        exhausted: !next,
        truncatedBySize: Boolean(page?.truncatedBySize),
        aroundHonoured: Boolean(around) && page?.aroundMessageId === around,
        clientAttributions,
        clientAttributionsTruncated: Boolean(page?.clientAttributionsTruncated),
    };
}

// The record read's attribution cap, mirroring api-server's: entries past it
// are dropped by the server too, which says so in clientAttributionsTruncated.
const MAX_RECORD_CLIENT_ATTRIBUTIONS = 128;
// The most observations one source-facts batch keeps; a longer page voids the
// server's per-id truncation list because the slice can drop any id's rows.
const MAX_SOURCE_FACT_OBSERVATIONS = 512;

// A wire ClientAttribution as the readers keep it: ids as UUIDs, bundles as sent.
function normalizeAttribution(entry) {
    return {
        sourceEventId: entry?.sourceEventId ? endpointIdToUuid(entry.sourceEventId) : null,
        invocationId: entry?.invocationId ? endpointIdToUuid(entry.invocationId) : null,
        factBundles: Array.isArray(entry?.factBundles) ? entry.factBundles : [],
    };
}

// One bounded batch for list decoration, using the same source bindings as record reads.
export async function readConversationSourceFacts({ sourceEventIds = [], traceIds = [], timeRange, signal } = {}) {
    const response = await authedFetch("/v1/conversations:readSourceFacts", {
        method: "POST", headers: { "Content-Type": "application/json" }, signal,
        body: JSON.stringify({
            sourceEventIds: sourceEventIds.map(uuidToEndpointId),
            traceIds: traceIds.map(uuidToEndpointId), timeRange,
        }),
    });
    if (!response.ok) throw new Error("Source evidence could not be loaded");
    const page = await response.json();
    const overflow = page.observations?.length > MAX_SOURCE_FACT_OBSERVATIONS;
    const named = traceIds.length ? page.truncatedTraceIds : page.truncatedSourceEventIds;
    return {
        observations: (Array.isArray(page.observations) ? page.observations : []).slice(0, MAX_SOURCE_FACT_OBSERVATIONS).map(row => ({
            traceId: endpointIdToUuid(row.traceId),
            ...normalizeAttribution(row.attribution),
        })),
        truncated: Boolean(page.truncated || overflow),
        // The slice above can drop any id's rows, so it voids the server's list.
        truncatedIds: !overflow && Array.isArray(named) ? named.map(endpointIdToUuid) : [],
    };
}

export async function fetchConversationRecord(body, signal) {
    const response = await authedFetch("/v1/conversations:readRecord", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), signal,
    });
    if (response.status === 404 || response.status === 405 || response.status === 501) {
        const error = new Error("The conversation record read is not served here.");
        error.recordRouteMissing = true;
        error.status = response.status;
        throw error;
    }
    if (!response.ok) {
        const error = new Error(`The conversation record could not be read (${response.status}).`);
        error.status = response.status;
        const text = await response.text().catch(() => "");
        try {
            const envelope = JSON.parse(text);
            if (typeof envelope?.message === "string") error.serverMessage = envelope.message;
        } catch { /* proxy page or empty body */ }
        if (body.includeSourceFacts === true && response.status === 400
            && /unknown field "includeSourceFacts"/.test(error.serverMessage || "")) {
            // An older gateway rejects new fields. Retry just once without
            // optional facts; other validation and authorization errors stand.
            const compatible = { ...body };
            delete compatible.includeSourceFacts;
            return fetchConversationRecord(compatible, signal);
        }
        throw error;
    }
    return response.json();
}

// A part the record served without its body: the body is above the read's
// per-part ceiling, so the message reads whole with this part as its size.
function elidedPartBlock(part) {
    const bytes = Number(part?.bodyBytes) || 0;
    // Decimal units, the way the record's sizes are quoted elsewhere.
    const size = bytes >= 1_000_000
        ? `${(bytes / 1_000_000).toFixed(1)} MB`
        : `${Math.max(1, Math.round(bytes / 1000))} KB`;
    return { type: "text", text: `This part's content (${size}) exceeds the view limit and is not shown.`, elided: true, bodyBytes: bytes };
}

async function readPageOnce({ traceId, timeRange, signal, cursor, exclude, maxMessages, focalEventId = null }) {
    const { query, content } = readers({ traceId, timeRange, signal });
    const excluded = new Set((exclude || []).map((id) => String(id).toLowerCase()));
    const newestFirst = [];
    let bound = cursor;
    // Whether rows older than the last window read may still exist. A window
    // that came back short with every row consumed says no; a window cut
    // short by a full page, or read full, says maybe.
    let more = true;
    let total = null;
    for (let round = 0; round < RECORD_PAGE_REFILL_ROUNDS; round++) {
        // Twice the shortfall, so a window that is half repeats still fills
        // the page in one round; one past that so "more" is a fact; never
        // more than the service returns.
        const want = Math.min(RECORD_MESSAGE_WINDOW, (maxMessages - newestFirst.length) * 2 + 1);
        const [rows, count] = await Promise.all([
            query(messagePageSpec(traceId, timeRange, recordKeysetBound(traceId, bound), want)),
            round === 0 && !cursor ? countMessages(query, traceId, timeRange, null).catch(() => null) : Promise.resolve(null),
        ]);
        if (round === 0) total = count;
        let consumed = 0;
        for (const row of rows) {
            consumed += 1;
            const id = String(row.messageId || "").toLowerCase();
            if (!id || excluded.has(id)) continue;
            excluded.add(id);
            newestFirst.push(row);
            if (newestFirst.length >= maxMessages) break;
        }
        if (rows.length < want) {
            // The window holds every remaining row, so exhaustion is a fact:
            // the record continues only if a row past the cut is a message the
            // axis has not shown.
            more = rows.slice(consumed).some((row) => {
                const id = String(row.messageId || "").toLowerCase();
                return id && !excluded.has(id);
            });
            break;
        }
        if (newestFirst.length >= maxMessages) break;
        const next = recordCursorOf(rows[rows.length - 1]);
        // A window that cannot advance (no usable keyset) ends the walk rather
        // than repeating it.
        if (!next || (bound && next.eventTime === bound.eventTime && next.ordinal === bound.ordinal)) { more = false; break; }
        bound = next;
    }
    const messages = newestFirst.slice().reverse();
    const oldest = newestFirst[newestFirst.length - 1] || null;
    const exhausted = !more;
    // A walk that hit the refill ceiling without filling anything hands back
    // the keyset it reached, so the next "earlier" continues from there rather
    // than claiming a record it did not finish reading is exhausted.
    const nextCursor = exhausted ? null : (oldest ? recordCursorOf(oldest) : bound);
    if (!messages.length) return { turns: [], messageCount: 0, total, nextCursor, exhausted };
    const focal = focalEventId ? String(focalEventId).toLowerCase() : null;
    const flags = focal
        ? (message) => ({ fromThisCapture: String(message.sourceEventId || "").toLowerCase() === focal })
        : undefined;
    const turns = await assembleWithinCeiling({ query, content, traceId, timeRange, flags }, messages);
    return { turns, messageCount: messages.length, total, nextCursor, exhausted };
}

// Turns for `messages`, halving the set whenever its parts or its text exceed
// what one read carries, so one heavy stretch costs a few extra reads rather
// than the page. A single message over the ceiling becomes a turn that says so.
async function assembleWithinCeiling(io, messages) {
    try {
        return await assembleRecordTurns({ ...io, messages }, io.flags);
    } catch (error) {
        if (!error?.recordTooLarge) throw error;
        if (messages.length === 1) {
            const [message] = messages;
            const captured = parseServerTimestamp(message.eventTime);
            return decorateConversationTurns([{
                role: message.role || "unknown", label: message.role || "unknown",
                blocks: [{ type: "text", text: "This message's content exceeds the view limit and is not shown." }],
                messageId: message.messageId, invocationId: null, sourceEventId: message.sourceEventId || null, contentSha256: null,
                ordinal: 0, usage: "", sectionKind: "conversation",
                messageAt: null, observationSourceEventId: message.sourceEventId || null, oversized: true,
                capturedAt: Number.isFinite(captured) ? captured : null,
            }]);
        }
        const half = Math.ceil(messages.length / 2);
        const [older, newer] = await Promise.all([
            assembleWithinCeiling(io, messages.slice(0, half)),
            assembleWithinCeiling(io, messages.slice(half)),
        ]);
        return [...older, ...newer];
    }
}
